import { useSelector } from 'react-redux';
import { RegistrationStepCredentials } from './Steps/RegistrationStepCredentials';
import { RegistrationStepCompanyData } from './Steps/RegistrationStepCompanyData';
import { RegistrationStepLegalAddress } from './Steps/RegistrationStepLegalAddress';
import { RegistrationStepDeliveryAddress } from './Steps/RegistrationStepDeliveryAddress';
import { RegistrationStepConfirmation } from './Steps/RegistrationStepConfirmation';
import { RegistrationStepContactPerson } from './Steps/RegistrationStepContactPerson';

import ErrorPage from './Steps/ErrorPage';
import AuthLayout from '@shared/ui/access-layout';
import { selectCurrentStep } from '../../entities/registration/model';
import { RegistrationStepShowAllData } from './Steps/RegistrationStepShowAllData';
import { selectUser } from '../../entities/auth/model';
import { clearRememberMeData } from '../../entities/auth/lib';
import { Button } from '@shared/ui/button';
import { User } from '../../types/interfaces';

// Группы, которым нельзя проходить регистрацию компании. Идентичный
// набор есть на бэке в RegisterCompanySerializer._BLOCKED_REGISTRATION_GROUPS.
// Дублируем, чтобы не ловить 403 в самом конце процесса, а сразу
// показать понятную заглушку и кнопку выйти из аккаунта.
const BLOCKED_REGISTRATION_GROUPS = new Set([
  'MANAGER',
  'OFFICE_MANAGER',
  'SALES_DIRECTOR',
  'HD_HEAD',
  'HD_ENGINEER',
  'HD_SERVICE_ENGINEER',
  'admins',
  'hd_lead_operator',
  'hd_operator',
  'managers',
]);

const isInternalAccount = (me: User | null | undefined): boolean => {
  if (!me) return false;
  if (me.is_superuser || me.is_staff) return true;
  return (me.groups ?? []).some((g) => BLOCKED_REGISTRATION_GROUPS.has(g));
};

const Registration = () => {
  const currentStep = useSelector(selectCurrentStep);
  const me = useSelector(selectUser);

  // Заглушка для случая «залогинен сотрудник пытается завершить
  // регистрацию компании». Без неё бэк завернёт всю процедуру 403'кой
  // на последнем шаге, и юзер не понимает, что делать. Здесь объясняем
  // и предлагаем выйти. Параллельная защита есть на бэке —
  // RegisterCompanySerializer.create перепроверит и не пустит даже
  // в обход фронта.
  if (isInternalAccount(me)) {
    const onLogout = () => {
      clearRememberMeData();
      window.location.reload();
    };
    return (
      <AuthLayout>
        <div className="flex flex-col items-start gap-4 max-w-[520px]">
          <h2 className="text-2xl font-bold text-[#141414]">
            Нельзя зарегистрировать компанию из этого аккаунта
          </h2>
          <p className="text-base text-[#4a4a4a] leading-relaxed">
            Вы залогинены под служебным аккаунтом FMeter (менеджер,
            админ или оператор). Регистрацию компании по инвайту
            должен проходить будущий владелец компании в чистой сессии.
          </p>
          <p className="text-base text-[#4a4a4a] leading-relaxed">
            Выйдите из аккаунта и откройте ссылку из инвайта снова — либо
            попросите клиента сделать это самостоятельно.
          </p>
          <Button onClick={onLogout}>Выйти из аккаунта</Button>
        </div>
      </AuthLayout>
    );
  }

  const render = () => {
    if (currentStep === 0) {
      return <RegistrationStepCredentials />;
    } else if (currentStep === 1) {
      return <RegistrationStepCompanyData />;
    } else if (currentStep === 2) {
      return <RegistrationStepLegalAddress />;
    } else if (currentStep === 3) {
      return <RegistrationStepDeliveryAddress />;
    } else if (currentStep === 4) {
      return <RegistrationStepContactPerson />;
    } else if (currentStep === 5) {
      return <RegistrationStepShowAllData />;
    } else if (currentStep === 6) {
      return <RegistrationStepConfirmation/>;
    }
    return null;
  };
  return currentStep === 8 ? (
    <ErrorPage />
  ) : (
    <AuthLayout>{render()}</AuthLayout>
  );
};

export default Registration;
